/**
 * The progress bar's percentage: `value` normalised against `max`, clamped to 0-100.
 *
 * A named pure function rather than three lines inline, because the arithmetic has a trap that cost
 * a red pipeline. `Progress` accepts Radix's props, which include `max`, and used to treat `value`
 * as a percentage outright — so value 50 with max 200 painted and announced 50% instead of 25%.
 * Normalising fixed that and broke something subtler: this string is RENDERED, and
 * `(25.87 / 100) * 100` is `25.869999999999997` in binary floating point, which is what the page
 * then displayed.
 *
 * So the default-max path does no arithmetic at all, and the normalising path rounds to four
 * decimal places — finer than any percentage this component displays, and enough to drop the
 * artefacts.
 */
export function toPercentage(value: number, max?: number): number {
  // NaN first. `Math.min(100, Math.max(0, NaN))` is NaN, which this component RENDERS — as the text
  // `NaN%` and as `translateX(-NaN%)`. Callers reach these numbers from API payloads, so a missing
  // field arrives as NaN rather than as undefined, which is the same class of defect this MR fixed
  // twice in BarChart.
  if (!Number.isFinite(value)) return 0;

  const ceiling = typeof max === 'number' && max > 0 ? max : 100;
  const raw = ceiling === 100 ? value : (value / ceiling) * 100;

  return Math.min(100, Math.max(0, Math.round(raw * 1e4) / 1e4));
}
